import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  getReasonTypeLabelParts,
  groupByReasonCode
} from "../utils/rfqReasonUtils";

const RFQReasonsInline = ({ items, onPartClick }) => {
  const [expandedReasons, setExpandedReasons] = useState(new Set());

  const toggleExpand = (reasonKey) => {
    setExpandedReasons(prev => {
      const next = new Set(prev);
      if (next.has(reasonKey)) {
        next.delete(reasonKey);
      } else {
        next.add(reasonKey);
      }
      return next;
    });
  };

  const isExpanded = (reasonKey) => expandedReasons.has(reasonKey);

  if (!items || items.length === 0) {
    return null;
  }

  const groupedReasons = groupByReasonCode(items);

  return (
    <div className="rfq-reasons-table" style={{ borderTop: '1px solid #e9ecef' }}>
      {Object.entries(groupedReasons).map(([reasonCode, affectedItems]) => {
        const reasonKey = reasonCode;
        const expanded = isExpanded(reasonKey);

        // Extract limit from the first item's reason (all items in this group have the same limit)
        const firstItemReason = affectedItems[0]?.reason;
        const limit = firstItemReason?.limit || null;

        const labelParts = getReasonTypeLabelParts(reasonCode, limit);

        return (
          <div key={reasonKey} className="rfq-table-item" style={{ borderBottom: '1px solid #e9ecef' }}>
            <div
              className={`rfq-table-cell-name p-2 d-flex align-items-start justify-content-between ${expanded ? 'bg-light' : ''}`}
              onClick={() => toggleExpand(reasonKey)}
              style={{
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!expanded) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (!expanded) {
                  e.currentTarget.style.backgroundColor = '';
                }
              }}
            >
              <div className="d-flex align-items-start flex-grow-1">
                <Icon
                  icon="mdi:chevron-down"
                  style={{
                    fontSize: '20px',
                    color: '#6c757d',
                    transition: 'transform 0.2s ease',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    marginRight: '8px',
                    marginTop: '2px',
                    flexShrink: 0
                  }}
                />
                <div className="flex-grow-1">
                  <div className="fw-bold small">
                    {labelParts.main}
                    {labelParts.threshold && (
                      <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '6px' }}>
                        ({labelParts.threshold})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    {affectedItems.length} part{affectedItems.length !== 1 ? 's' : ''} affected
                  </div>
                </div>
              </div>
            </div>
            {expanded && (
              <div className="rfq-table-cell-reasons p-2 bg-white" style={{ borderTop: '1px solid #e9ecef' }}>
                <ul className="mb-0 ps-3 small" style={{ listStyle: 'none', paddingLeft: '0' }}>
                  {affectedItems.slice(0, 5).map((affectedItem, idx) => {
                    // part_code now contains the stable identifier (last 3 segments) from backend
                    const stableIdentifier = affectedItem.part_code || 'N/A';
                    const isClickable = onPartClick && affectedItem.canFocus === true;

                    const handlePartClick = (e) => {
                      if (isClickable) {
                        e.stopPropagation();
                        onPartClick({
                          itemId: affectedItem.item_id,
                          stableIdentifier,
                          itemName: affectedItem.item_name,
                          item: affectedItem
                        });
                      }
                    };

                    return (
                      <li key={idx} className="mb-2 d-flex align-items-start">
                        <span className="me-2" style={{ color: '#6c757d', fontSize: '0.75rem' }}>•</span>
                        <div className="flex-grow-1">
                          <div className="fw-bold" style={{ fontSize: '0.875rem' }}>
                            {affectedItem.item_name}
                          </div>
                          {stableIdentifier !== 'N/A' && (
                            <div
                              className="d-flex align-items-center gap-1"
                              onClick={isClickable ? handlePartClick : undefined}
                              style={{
                                fontSize: '0.75rem',
                                color: '#3c5b8a',
                                fontWeight: '500',
                                cursor: isClickable ? 'pointer' : 'default',
                                display: 'inline-flex'
                              }}
                              title={isClickable ? 'Click to focus on this part' : undefined}
                            >
                              {stableIdentifier}
                              {isClickable && (
                                <Icon
                                  icon="heroicons:arrow-top-right-on-square-20-solid"
                                  style={{
                                    fontSize: '14px',
                                    color: '#3c5b8a'
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {affectedItems.length > 5 && (
                  <div className="mt-2 ps-3" style={{ fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
                    and {affectedItems.length - 5} more part{affectedItems.length - 5 !== 1 ? 's' : ''}... (view all in RFQ details)
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RFQReasonsInline;
